import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import Cookies from "js-cookie";
import JoditEditor from "jodit-react";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

const EMPTY_FORM = {
  title: "",
  h1: "",
  slug: "",
  excerpt: "",
  content: "",
  meta_title: "",
  meta_description: "",
  canonical_url: "",
  image_url: "",
  og_title: "",
  og_description: "",
  og_image: "",
  status: "draft",
  published_at: "",
  publicId: "",
};

const ADMIN_API_ROOT = `${process.env.React_APP_BACKEND_URL}/api/admin`;
const API_BASE = `${ADMIN_API_ROOT}/articles`;
const SLUG_CHECK_URL = `${ADMIN_API_ROOT}/article-slug-availability`;

const authConfig = () => ({
  headers: { Authorization: `Bearer ${Cookies.get("admintoken")}` },
});

const slugify = (value) =>
  String(value || "")
    .normalize("NFKC")
    .trim()
    .toLocaleLowerCase()
    .replace(/[\s_]+/gu, "-")
    .replace(/[^\p{L}\p{N}-]+/gu, "")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");

const plainText = (html) =>
  String(html || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();

const toDateTimeLocal = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const offset = date.getTimezoneOffset();
  return new Date(date.getTime() - offset * 60_000).toISOString().slice(0, 16);
};

const getValidationMessages = (error) => {
  const errors = error?.response?.data?.errors;
  if (errors && typeof errors === "object") {
    return Object.values(errors).flat().join(" ");
  }
  return error?.response?.data?.message || error?.message || "Something went wrong.";
};

function CreateArticles() {
  const editorRef = useRef(null);
  const formSectionRef = useRef(null);
  const editHeadingRef = useRef(null);
  const submittingRef = useRef(false);
  const [articles, setArticles] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingArticle, setEditingArticle] = useState(null);
  const [slugEdited, setSlugEdited] = useState(false);
  const [slugAvailable, setSlugAvailable] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState("");

  const editorConfig = useMemo(
    () => ({
      readonly: false,
      toolbar: true,
      spellcheck: true,
      language: "en",
      toolbarButtonSize: "small",
      toolbarAdaptive: true,
      showCharsCounter: true,
      showWordsCounter: true,
      showXPathInStatusbar: false,
      askBeforePasteHTML: true,
      askBeforePasteFromWord: true,
      uploader: { insertImageAsBase64URI: true },
      height: 360,
    }),
    []
  );

  const loadArticles = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await axios.get(`${API_BASE}?per_page=100`, authConfig());
      setArticles(response.data?.data || []);
    } catch (requestError) {
      setError(getValidationMessages(requestError));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadArticles();
  }, [loadArticles]);

  useEffect(() => {
    if (!editingArticle) return undefined;

    const frameId = window.requestAnimationFrame(() => {
      formSectionRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });

      window.setTimeout(() => {
        editHeadingRef.current?.focus({ preventScroll: true });
      }, 350);
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [editingArticle]);

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setEditingArticle(null);
    setSlugEdited(false);
    setSlugAvailable(null);
    setImageFile(null);
    setUploadProgress(0);
    setError("");
  };

  const updateField = (name, value) => {
    setForm((current) => {
      const next = { ...current, [name]: value };

      if (name === "title" && !slugEdited && !editingArticle) {
        next.slug = slugify(value);
      }
      if (name === "title" && !current.h1) next.h1 = value;
      if (name === "title" && !current.meta_title) next.meta_title = value;
      if (name === "title" && !current.og_title) next.og_title = value;
      if (name === "excerpt" && !current.meta_description) next.meta_description = value;
      if (name === "excerpt" && !current.og_description) next.og_description = value;
      if (name === "image_url" && !current.og_image) next.og_image = value;

      return next;
    });
  };

  const checkSlug = async () => {
    const normalized = slugify(form.slug);
    updateField("slug", normalized);

    if (!normalized) {
      setSlugAvailable(false);
      return;
    }

    try {
      const response = await axios.get(SLUG_CHECK_URL, {
        ...authConfig(),
        params: {
          slug: normalized,
          ignore_id: editingArticle?.id || undefined,
        },
      });
      setSlugAvailable(Boolean(response.data?.available));
    } catch (requestError) {
      setSlugAvailable(null);
      setError(getValidationMessages(requestError));
    }
  };

  const uploadImage = async () => {
    if (!imageFile) {
      return { url: form.image_url, publicId: form.publicId };
    }

    const cloudName =
      process.env.REACT_APP_CLOUDINARY_CLOUD_NAME ||
      process.env.React_App_CLOUDINARY_CLOUD_NAME;
    const uploadPreset =
      process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET ||
      process.env.React_App_CLOUDINARY_UPLOAD_PRESET;

    if (!cloudName || !uploadPreset) {
      throw new Error(
        "Cloudinary is not configured. Add REACT_APP_CLOUDINARY_CLOUD_NAME and REACT_APP_CLOUDINARY_UPLOAD_PRESET, or enter an image URL."
      );
    }

    const uploadData = new FormData();
    uploadData.append("file", imageFile);
    uploadData.append("upload_preset", uploadPreset);

    const response = await axios.post(
      `https://api.cloudinary.com/v1_1/${cloudName}/image/upload`,
      uploadData,
      {
        onUploadProgress: (event) => {
          if (event.total) {
            setUploadProgress(Math.round((event.loaded / event.total) * 100));
          }
        },
      }
    );

    return {
      url: response.data.secure_url,
      publicId: response.data.public_id,
    };
  };

  const validateClientSide = () => {
    if (form.title.trim().length < 3) return "Article title must contain at least 3 characters.";
    if (!slugify(form.slug)) return "A valid URL slug is required.";
    if (plainText(form.content).length < 20) {
      return "Article content must contain at least 20 readable characters.";
    }
    if (form.meta_description.length > 320 || form.og_description.length > 320) {
      return "SEO and Open Graph descriptions cannot exceed 320 characters.";
    }
    return "";
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    // Prevent a rapid double-click from starting two overlapping saves before
    // React has applied the disabled state to the submit button.
    if (submittingRef.current) return;

    setError("");
    setMessage(null);

    const validationError = validateClientSide();
    if (validationError) {
      setError(validationError);
      return;
    }

    submittingRef.current = true;
    setSaving(true);
    try {
      const uploaded = await uploadImage();
      const payload = {
        ...form,
        slug: slugify(form.slug),
        h1: form.h1.trim() || form.title.trim(),
        image_url: uploaded.url || null,
        og_image: form.og_image || uploaded.url || null,
        publicId: uploaded.publicId || null,
        published_at: form.status === "published" ? form.published_at || null : null,
      };

      if (editingArticle) {
        await axios.put(
          `${API_BASE}/${encodeURIComponent(editingArticle.slug)}`,
          payload,
          authConfig()
        );
        setMessage({ severity: "success", text: "Article updated successfully." });
      } else {
        await axios.post(API_BASE, payload, authConfig());
        setMessage({ severity: "success", text: "Article created successfully." });
      }

      resetForm();
      await loadArticles();
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (requestError) {
      setError(getValidationMessages(requestError));
    } finally {
      submittingRef.current = false;
      setSaving(false);
    }
  };

  const editArticle = (article) => {
    setEditingArticle(article);
    setSlugEdited(true);
    setSlugAvailable(true);
    setImageFile(null);
    setForm({
      title: article.title || "",
      h1: article.h1 || article.title || "",
      slug: article.slug || "",
      excerpt: article.excerpt || "",
      content: article.content || article.description || "",
      meta_title: article.meta_title || "",
      meta_description: article.meta_description || "",
      canonical_url: article.canonical_url || "",
      image_url: article.image_url || article.display_image || "",
      og_title: article.og_title || "",
      og_description: article.og_description || "",
      og_image: article.og_image || "",
      status: article.status || "draft",
      published_at: toDateTimeLocal(article.published_at),
      publicId: article.publicId || "",
    });
    setError("");
    setMessage(null);
  };

  const changePublication = async (article) => {
    const nextStatus = article.status === "published" ? "draft" : "published";
    setError("");
    try {
      const response = await axios.patch(
        `${API_BASE}/${encodeURIComponent(article.slug)}/publication`,
        { status: nextStatus },
        authConfig()
      );
      setMessage({ severity: "success", text: response.data.message });
      await loadArticles();
    } catch (requestError) {
      setError(getValidationMessages(requestError));
    }
  };

  const deleteArticle = async (article) => {
    const confirmed = window.confirm(
      `Delete “${article.title}”? This action cannot be undone.`
    );
    if (!confirmed) return;

    setError("");
    try {
      const response = await axios.delete(
        `${API_BASE}/${encodeURIComponent(article.slug)}`,
        authConfig()
      );
      if (editingArticle?.id === article.id) resetForm();
      setMessage({ severity: "success", text: response.data.message });
      await loadArticles();
    } catch (requestError) {
      setError(getValidationMessages(requestError));
    }
  };

  return (
    <Box
      dir="rtl"
      translate="no"
      className="notranslate"
      sx={{
        width: "min(1180px, 94%)",
        mx: "auto",
        my: 4,
        fontFamily: process.env.REACT_APP_FONT || "Assistant",
      }}
    >
      <Stack spacing={1} sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight={800}>
          ניהול מאמרים
        </Typography>
        <Typography color="text.secondary">
          יצירה, עריכה, פרסום, ביטול פרסום וניהול מלא של נתוני SEO לכל מאמר.
        </Typography>
      </Stack>

      {message && (
        <Alert severity={message.severity} sx={{ mb: 2 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      <Card
        ref={formSectionRef}
        variant="outlined"
        sx={{ mb: 4, borderRadius: 3, scrollMarginTop: 24 }}
      >
        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography
              ref={editHeadingRef}
              variant="h6"
              fontWeight={800}
              tabIndex={-1}
              aria-live="polite"
              sx={{ outline: "none" }}
            >
              {editingArticle
                ? `Editing - ${editingArticle.title}`
                : "יצירת מאמר חדש"}
            </Typography>
            {editingArticle && <Button onClick={resetForm}>ביטול עריכה</Button>}
          </Stack>

          <Box component="form" onSubmit={handleSubmit} noValidate>
            <Grid container spacing={2.5}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="כותרת המאמר"
                  value={form.title}
                  onChange={(event) => updateField("title", event.target.value)}
                  required
                  fullWidth
                  inputProps={{ maxLength: 255 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="H1"
                  value={form.h1}
                  onChange={(event) => updateField("h1", event.target.value)}
                  fullWidth
                  helperText="אם ריק, כותרת המאמר תשמש כ-H1."
                  inputProps={{ maxLength: 255 }}
                />
              </Grid>

              <Grid item xs={12} md={8}>
                <TextField
                  label="URL Slug"
                  value={form.slug}
                  onChange={(event) => {
                    setSlugEdited(true);
                    setSlugAvailable(null);
                    updateField("slug", event.target.value);
                  }}
                  onBlur={checkSlug}
                  required
                  fullWidth
                  error={slugAvailable === false}
                  helperText={
                    slugAvailable === true
                      ? "הכתובת פנויה."
                      : slugAvailable === false
                      ? "הכתובת אינה תקינה או שכבר נמצאת בשימוש."
                      : `כתובת לדוגמה: /blog/${slugify(form.slug) || "article-slug"}`
                  }
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel id="article-status-label">סטטוס</InputLabel>
                  <Select
                    labelId="article-status-label"
                    label="סטטוס"
                    value={form.status}
                    onChange={(event) => updateField("status", event.target.value)}
                  >
                    <MenuItem value="draft">טיוטה</MenuItem>
                    <MenuItem value="published">פורסם</MenuItem>
                  </Select>
                  <FormHelperText>טיוטות אינן מופיעות באתר או במפת האתר.</FormHelperText>
                </FormControl>
              </Grid>

              {form.status === "published" && (
                <Grid item xs={12} md={6}>
                  <TextField
                    label="מועד פרסום"
                    type="datetime-local"
                    value={form.published_at}
                    onChange={(event) => updateField("published_at", event.target.value)}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    helperText="אם ריק, זמן הפרסום יהיה הזמן הנוכחי."
                  />
                </Grid>
              )}

              <Grid item xs={12}>
                <TextField
                  label="תקציר"
                  value={form.excerpt}
                  onChange={(event) => updateField("excerpt", event.target.value)}
                  fullWidth
                  multiline
                  minRows={3}
                  inputProps={{ maxLength: 500 }}
                  helperText={`${form.excerpt.length}/500 — מוצג בכרטיס המאמר.`}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                  תוכן המאמר
                </Typography>
                <JoditEditor
                  ref={editorRef}
                  value={form.content}
                  config={editorConfig}
                  onBlur={(newContent) => updateField("content", newContent)}
                />
                <FormHelperText>
                  {plainText(form.content).length} תווים קריאים. תוכן מסוכן כגון script ו-event handlers יוסר גם בשרת.
                </FormHelperText>
              </Grid>

              <Grid item xs={12}>
                <Divider><Chip label="תמונה" /></Divider>
              </Grid>
              <Grid item xs={12} md={7}>
                <TextField
                  label="Article Image URL"
                  value={form.image_url}
                  onChange={(event) => updateField("image_url", event.target.value)}
                  fullWidth
                  type="url"
                />
              </Grid>
              <Grid item xs={12} md={5}>
                <Button component="label" variant="outlined" fullWidth sx={{ height: 56 }}>
                  {imageFile ? imageFile.name : "העלאת תמונה מהמחשב"}
                  <input
                    hidden
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/gif"
                    onChange={(event) => setImageFile(event.target.files?.[0] || null)}
                  />
                </Button>
                {uploadProgress > 0 && uploadProgress < 100 && (
                  <FormHelperText>העלאה: {uploadProgress}%</FormHelperText>
                )}
              </Grid>

              <Grid item xs={12}>
                <Divider><Chip label="SEO Metadata" /></Divider>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Meta Title"
                  value={form.meta_title}
                  onChange={(event) => updateField("meta_title", event.target.value)}
                  fullWidth
                  inputProps={{ maxLength: 255 }}
                  helperText={`${form.meta_title.length}/255`}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Canonical URL"
                  value={form.canonical_url}
                  onChange={(event) => updateField("canonical_url", event.target.value)}
                  fullWidth
                  type="url"
                  helperText="השאר ריק כדי להשתמש אוטומטית ב-/blog/slug."
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Meta Description"
                  value={form.meta_description}
                  onChange={(event) => updateField("meta_description", event.target.value)}
                  fullWidth
                  multiline
                  minRows={2}
                  inputProps={{ maxLength: 320 }}
                  helperText={`${form.meta_description.length}/320`}
                />
              </Grid>

              <Grid item xs={12}>
                <Divider><Chip label="Open Graph" /></Divider>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Open Graph Title"
                  value={form.og_title}
                  onChange={(event) => updateField("og_title", event.target.value)}
                  fullWidth
                  inputProps={{ maxLength: 255 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Open Graph Image URL"
                  value={form.og_image}
                  onChange={(event) => updateField("og_image", event.target.value)}
                  fullWidth
                  type="url"
                  helperText="אם ריק, תמונת המאמר תשמש אוטומטית."
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Open Graph Description"
                  value={form.og_description}
                  onChange={(event) => updateField("og_description", event.target.value)}
                  fullWidth
                  multiline
                  minRows={2}
                  inputProps={{ maxLength: 320 }}
                  helperText={`${form.og_description.length}/320`}
                />
              </Grid>

              <Grid item xs={12}>
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  fullWidth
                  disabled={saving || slugAvailable === false}
                  aria-busy={saving}
                  translate="no"
                  className="notranslate"
                  sx={{ py: 1.4, fontWeight: 800 }}
                >
                  {/*
                    Keep every label mounted. Browser translation extensions can
                    replace raw text nodes; conditionally removing those nodes can
                    then make React throw removeChild NotFoundError.
                  */}
                  <Stack
                    component="span"
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    aria-hidden={!saving}
                    sx={{ display: saving ? "inline-flex" : "none" }}
                  >
                    <CircularProgress size={20} color="inherit" />
                    <Box component="span">שומר...</Box>
                  </Stack>

                  <Box
                    component="span"
                    aria-hidden={saving || !editingArticle}
                    sx={{ display: !saving && editingArticle ? "inline" : "none" }}
                  >
                    שמירת שינויים
                  </Box>

                  <Box
                    component="span"
                    aria-hidden={saving || Boolean(editingArticle)}
                    sx={{ display: !saving && !editingArticle ? "inline" : "none" }}
                  >
                    יצירת מאמר
                  </Box>
                </Button>
              </Grid>
            </Grid>
          </Box>
        </CardContent>
      </Card>

      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5" fontWeight={800}>כל המאמרים</Typography>
        <Chip label={`${articles.length} מאמרים`} variant="outlined" />
      </Stack>

      {loading ? (
        <Box sx={{ display: "grid", placeItems: "center", py: 7 }}>
          <CircularProgress />
        </Box>
      ) : articles.length === 0 ? (
        <Alert severity="info">עדיין לא נוצרו מאמרים.</Alert>
      ) : (
        <Grid container spacing={2}>
          {articles.map((article) => (
            <Grid item xs={12} md={6} lg={4} key={article.id}>
              <Card variant="outlined" sx={{ height: "100%", borderRadius: 3, display: "flex", flexDirection: "column" }}>
                {article.display_image && (
                  <Box
                    component="img"
                    src={article.display_image}
                    alt=""
                    sx={{ width: "100%", aspectRatio: "16 / 8", objectFit: "cover" }}
                  />
                )}
                <CardContent sx={{ flexGrow: 1 }}>
                  <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                    <Chip
                      size="small"
                      color={article.status === "published" ? "success" : "default"}
                      label={article.status === "published" ? "פורסם" : "טיוטה"}
                    />
                  </Stack>
                  <Typography variant="h6" fontWeight={800} gutterBottom>
                    {article.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    /blog/{article.slug}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {article.excerpt || "ללא תקציר"}
                  </Typography>
                </CardContent>
                <CardActions sx={{ px: 2, pb: 2, flexWrap: "wrap" }}>
                  <Button size="small" onClick={() => editArticle(article)}>עריכה</Button>
                  <Button size="small" onClick={() => changePublication(article)}>
                    {article.status === "published" ? "ביטול פרסום" : "פרסום"}
                  </Button>
                  {article.status === "published" && (
                    <Button
                      size="small"
                      component="a"
                      href={article.url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      צפייה
                    </Button>
                  )}
                  <Button size="small" color="error" onClick={() => deleteArticle(article)}>
                    מחיקה
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
}

export default CreateArticles;